import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { DollarSign, TrendingDown, TrendingUp, Briefcase, Phone, User as UserIcon, Calendar as CalIcon, Trash2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const Card = ({ icon: Icon, label, value, tone = "primary", testId }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center border ${tone === "destructive" ? "border-destructive/40 bg-destructive/10 text-destructive" : tone === "success" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-primary/40 bg-primary/10 text-primary"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className={`mt-3 text-3xl font-bold tracking-tight ${tone === "destructive" ? "text-destructive" : tone === "success" ? "text-emerald-400" : "text-foreground"}`} style={{ fontFamily: "Outfit" }}>
      {value}
    </div>
  </div>
);

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function ReportsPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [calls, setCalls] = useState({ total: 0, by_source: [], by_day: [] });
  const todayIso = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState("");
  const [client, setClient] = useState("all");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const fetchTrash = () => {
    setTrashLoading(true);
    api.get("/jobs/trash")
      .then((r) => setTrash(r.data || []))
      .finally(() => setTrashLoading(false));
  };

  const openTrash = () => { setTrashOpen(true); fetchTrash(); };

  const restoreJob = async (id) => {
    try {
      await api.post(`/jobs/${id}/restore`);
      fetchTrash();
      fetchReport();
    } catch (e) {
      alert("Restore failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const purgeTrash = async () => {
    if (!window.confirm("Permanently delete ALL jobs in the trash? This cannot be undone.")) return;
    try {
      const res = await api.delete("/jobs/trash/purge");
      alert(`Permanently deleted ${res.data.purged} jobs.`);
      fetchTrash();
    } catch (e) {
      alert("Purge failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const fetchReport = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (client && client !== "all") params.client = client;
    setLoading(true);
    api.get("/reports/summary", { params })
      .then((r) => {
        setData(r.data);
        if (r.data?.clients?.length !== undefined) setClients(r.data.clients);
      })
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, client]);

  useEffect(() => {
    fetchReport();
    api.get("/track/call/summary").then((r) => {
      const d = r.data || { total: 0, by_source: [], by_day: [] };
      d.by_source = (d.by_source || []).filter((x) => !String(x.source).startsWith("TEST_"));
      setCalls(d);
    }).catch(() => {});
  }, [fetchReport]);

  const resetFilters = () => {
    setDateFrom(todayIso);
    setDateTo("");
    setClient("all");
  };
  const showAllTime = () => {
    setDateFrom("2020-01-01");
    setDateTo("");
  };
  const wipeHistory = async () => {
    try {
      const res = await api.delete("/jobs/bulk", { params: { before: todayIso } });
      alert(`Moved ${res.data.deleted} old jobs to Trash (before ${todayIso}). Open Trash to restore.`);
      setConfirmWipe(false);
      fetchReport();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  if (!data) return <div className="text-muted-foreground">{t.common.loading}</div>;

  return (
    <div data-testid="reports-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
          {t.portal.reports.title}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t.portal.reports.subtitle}</p>
      </div>

      {/* Filters */}
      <div className="mt-6 rounded-xl border border-border bg-card p-4" data-testid="reports-filters">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 mb-1">
              <CalIcon className="h-3 w-3" /> From
            </label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-background" data-testid="reports-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 mb-1">
              <CalIcon className="h-3 w-3" /> To (optional)
            </label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-background" data-testid="reports-date-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 mb-1">
              <UserIcon className="h-3 w-3" /> Client
            </label>
            <Select value={client} onValueChange={setClient}>
              <SelectTrigger className="bg-background" data-testid="reports-client-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-72">
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={showAllTime} className="rounded-full flex-1" data-testid="reports-alltime-btn">All-time</Button>
            <Button variant="outline" onClick={resetFilters} className="rounded-full flex-1" data-testid="reports-today-btn">Today+</Button>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-muted-foreground">
            Showing <span className="text-foreground font-semibold">{data.totals.jobs} jobs</span>
            {" · "}from <span className="text-foreground">{data.filters.date_from || "any"}</span>
            {data.filters.date_to && <> to <span className="text-foreground">{data.filters.date_to}</span></>}
            {" · "}client: <span className="text-foreground">{data.filters.client}</span>
            {loading && <span className="ml-2 text-primary">Refreshing...</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={openTrash}
              className="rounded-full text-xs h-7 flex items-center gap-1"
              data-testid="reports-trash-btn"
            >
              <Trash2 className="h-3 w-3" /> Trash
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmWipe(true)}
              className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10 text-xs h-7"
              data-testid="reports-wipe-btn"
            >
              Move old jobs to trash
            </Button>
          </div>
        </div>
      </div>

      {confirmWipe && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Move all jobs before today to Trash?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Every job with a date before <span className="text-foreground font-semibold">{todayIso}</span> will be moved to the Trash. You can restore them from the Trash panel at any time.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmWipe(false)} className="rounded-full">Cancel</Button>
              <Button onClick={wipeHistory} className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground" data-testid="reports-wipe-confirm-btn">Move to Trash</Button>
            </div>
          </div>
        </div>
      )}

      {trashOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="trash-modal">
          <div className="bg-card border border-border rounded-2xl max-w-2xl w-full p-6 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <Trash2 className="h-5 w-5 text-destructive" /> Trash
                <span className="text-xs text-muted-foreground font-normal ml-1">({trash.length})</span>
              </h3>
              <button onClick={() => setTrashOpen(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Deleted jobs are kept here. Restore them or purge permanently.</p>

            <div className="mt-4 flex-1 overflow-y-auto space-y-2 pr-1">
              {trashLoading && <div className="text-sm text-muted-foreground text-center py-6">Loading…</div>}
              {!trashLoading && trash.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">The trash is empty.</div>
              )}
              {trash.map((j) => (
                <div key={j.id} className="rounded-lg border border-border p-3 flex items-center justify-between gap-3" data-testid={`trash-row-${j.id}`}>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{j.date} · {j.start_time}</div>
                    <div className="font-semibold truncate">{j.client_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{j.service_type} · ${(j.total_payment || 0).toFixed(2)}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => restoreJob(j.id)}
                    className="rounded-full text-xs h-7 flex-shrink-0"
                    data-testid={`trash-restore-${j.id}`}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={purgeTrash}
                disabled={trash.length === 0}
                className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10 text-xs h-8"
                data-testid="trash-purge-btn"
              >
                Purge all permanently
              </Button>
              <Button variant="outline" size="sm" onClick={() => setTrashOpen(false)} className="rounded-full text-xs h-8">Close</Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={DollarSign} label={t.portal.reports.income} value={fmt(data.totals.income)} tone="success" testId="stat-income" />
        <Card icon={TrendingDown} label={t.portal.reports.expenses} value={fmt(data.totals.expenses)} tone="destructive" testId="stat-expenses" />
        <Card icon={TrendingUp} label={t.portal.reports.profit} value={fmt(data.totals.profit)} tone="primary" testId="stat-profit" />
        <Card icon={Briefcase} label={t.portal.reports.jobs} value={data.totals.jobs} tone="primary" testId="stat-jobs" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>{t.portal.reports.monthly}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">Income vs Expenses per month · profit line</div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.monthly} margin={{ top: 10, right: 20, bottom: 0, left: -10 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="period" stroke="#71717A" fontSize={12} tickLine={false} />
                <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => (v >= 1000 ? `$${(v/1000).toFixed(v%1000===0?0:1)}k` : `$${v}`)} />
                <Tooltip
                  contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8 }}
                  formatter={(v, n) => [`$${Number(v).toLocaleString()}`, n]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" fill="#F26B1F" name="Income" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" fill="#EF4444" name="Expenses" radius={[6, 6, 0, 0]} />
                <Bar dataKey="profit" fill="#4ADE80" name="Profit" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {data.monthly.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-6">No jobs in the selected range.</div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>{t.portal.reports.weekly}</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.weekly} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="period" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={12} />
                <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8 }} />
                <Legend />
                <Bar dataKey="income" fill="#F26B1F" name="Income" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" fill="#EF4444" name="Expenses" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-border bg-card p-4 md:p-6">
        <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>{t.portal.reports.byService}</h3>
        {data.by_service.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No data yet — add your first job.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.by_service} layout="vertical" margin={{ top: 5, right: 20, bottom: 0, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis type="number" stroke="#71717A" fontSize={12} />
                <YAxis dataKey="service" type="category" stroke="#71717A" fontSize={11} width={160} />
                <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8 }} />
                <Bar dataKey="income" fill="#F26B1F" name="Income" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* By client breakdown */}
      <div className="mt-4 rounded-xl border border-border bg-card p-4 md:p-6" data-testid="reports-by-client">
        <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <UserIcon className="h-4 w-4 text-primary" /> Top clients
        </h3>
        {(data.by_client || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No data for the selected filters.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.by_client.slice(0, 12).map((c) => (
              <div key={c.client} className="rounded-lg border border-border p-4" data-testid={`client-row-${c.client}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center text-primary text-sm font-bold">
                    {c.client?.slice(0,1)?.toUpperCase()}
                  </div>
                  <div className="font-semibold truncate">{c.client}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><div className="text-muted-foreground">Income</div><div className="font-bold text-primary">${c.income.toLocaleString(undefined,{maximumFractionDigits:0})}</div></div>
                  <div><div className="text-muted-foreground">Profit</div><div className="font-bold text-emerald-400">${c.profit.toLocaleString(undefined,{maximumFractionDigits:0})}</div></div>
                  <div><div className="text-muted-foreground">Expenses</div><div className="font-bold text-destructive">${c.expenses.toLocaleString(undefined,{maximumFractionDigits:0})}</div></div>
                  <div><div className="text-muted-foreground">Jobs</div><div className="font-bold">{c.jobs}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call tracking */}
      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="call-tracking-total">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Call clicks (all time)</div>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center border border-primary/40 bg-primary/10 text-primary">
              <Phone className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-3 text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {calls?.total || 0}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Taps/clicks on your phone number, WhatsApp and Messenger buttons across the site.</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">By source</div>
          {!calls?.by_source?.length ? (
            <div className="text-sm text-muted-foreground py-4 text-center">No clicks yet.</div>
          ) : (
            <div className="space-y-2">
              {calls.by_source.map((row) => {
                const total = calls.total || 1;
                const pct = Math.round((row.count / total) * 100);
                return (
                  <div key={row.source} data-testid={`call-source-${row.source}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{row.source.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground">{row.count} · {pct}%</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
